import { isErrorResult, isSuppressedTool } from '@/components/assistant-ui/message-part-utils';

interface MessagePart {
  type: string;
  toolName?: string;
  isError?: boolean;
  result?: unknown;
}

interface MessageLike {
  status?: { type: string; reason?: string; error?: unknown };
  parts: readonly MessagePart[];
}

function isToolCallErrored(part: MessagePart): boolean {
  return part.isError === true || isErrorResult(part.result);
}

/**
 * Decides whether the generic `MessageError` UI should render for an
 * assistant message that ended in `incomplete: error`. Returns `false`
 * (suppress) when every errored tool call in the message is for the
 * Mastra-internal `updateWorkingMemory` tool — its validator failures are
 * recoverable noise that the model retries internally and should not
 * surface as a user-facing error. Any visible-tool failure or non-tool
 * error returns `true` so the existing error UI is preserved.
 *
 * @param message Reactive assistant-ui message snapshot, including
 *   `status` and `parts`. Tool errors are detected via `part.isError` or
 *   an error-shaped `result` (matches `use-tool-group-state`).
 * @returns `true` to render the generic error, `false` to suppress it.
 */
export function shouldRenderMessageError(message: MessageLike): boolean {
  const status = message.status;
  if (status?.type !== 'incomplete' || status.reason !== 'error') return false;
  if (!status.error) return false;

  let foundErroredToolCall = false;
  for (const part of message.parts) {
    if (part.type !== 'tool-call' || !isToolCallErrored(part)) continue;
    if (!isSuppressedTool(part.toolName ?? '')) return true;
    foundErroredToolCall = true;
  }
  return !foundErroredToolCall;
}
